import { HttpClient } from '@adapters/HttpClient';
import {
  CreateManualTransactionPayload,
  HoldedChartAccount,
  ManualTransactionCreated,
  WalletDetail,
  WalletTransactionsResponse,
} from '../models/Wallet';

export type WalletTransactionsAdvancedFilterValue = string | number | Array<string | number>;

export interface WalletTransactionsAdvancedFilter {
  column: string;
  operator: string;
  value?: WalletTransactionsAdvancedFilterValue;
}

export interface WalletTransactionsAdvancedFilters {
  filters: WalletTransactionsAdvancedFilter[];
  logic?: 'AND' | 'OR';
}

export interface WalletTransactionsFilters {
  page?: number;
  perPage?: number;
  advanced_filters?: WalletTransactionsAdvancedFilters;
  sorting?: {
    column: string;
    direction: 'asc' | 'desc';
  };
}

const buildAdvancedFiltersParams = (advancedFilters: WalletTransactionsAdvancedFilters) => {
  const params: Record<string, string | number> = {};

  advancedFilters.filters.forEach((filter, index) => {
    params[`advanced_filters[filters][${index}][column]`] = filter.column;
    params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

    if (
      filter.value === undefined ||
      filter.value === null ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    ) {
      return;
    }

    if (Array.isArray(filter.value)) {
      filter.value.forEach((value, valueIndex) => {
        params[`advanced_filters[filters][${index}][value][${valueIndex}]`] = value;
      });
      return;
    }

    params[`advanced_filters[filters][${index}][value]`] = filter.value;
  });

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
};

export class WalletApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getWalletDetail(walletId: number): Promise<WalletDetail> {
    return await this.httpClient.get<WalletDetail>(`/wallets/${walletId}`, {}, {}, true);
  }

  async getWalletTransactions(
    walletId: number,
    filters: WalletTransactionsFilters = {},
  ): Promise<WalletTransactionsResponse> {
    const { page = 1, perPage = 50, advanced_filters, sorting } = filters;
    const sortColumn = sorting?.column ?? 'created_at';
    const sortDirection = sorting?.direction ?? 'desc';

    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };

    if (advanced_filters && advanced_filters.filters.length > 0) {
      Object.assign(
        params,
        buildAdvancedFiltersParams({
          logic: advanced_filters.logic ?? 'AND',
          filters: advanced_filters.filters,
        }),
      );
    }

    return await this.httpClient.get<WalletTransactionsResponse>(`/wallets/${walletId}/transactions`, params, {}, true);
  }

  async exportWalletTransactions(
    walletId: number,
    filters: Pick<WalletTransactionsFilters, 'advanced_filters' | 'sorting'> = {},
  ): Promise<{ url_file: string }> {
    const { advanced_filters, sorting } = filters;
    const sortColumn = sorting?.column ?? 'created_at';
    const sortDirection = sorting?.direction ?? 'desc';

    const params: Record<string, string | number> = {
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };

    if (advanced_filters && advanced_filters.filters.length > 0) {
      Object.assign(
        params,
        buildAdvancedFiltersParams({
          logic: advanced_filters.logic ?? 'AND',
          filters: advanced_filters.filters,
        }),
      );
    }

    return await this.httpClient.get<{ url_file: string }>(
      `/wallets/${walletId}/transactions/export`,
      params,
      {},
      true,
    );
  }

  async getHoldedChartOfAccounts(): Promise<HoldedChartAccount[]> {
    return await this.httpClient.get<HoldedChartAccount[]>('/wallets/holded/chart-of-accounts', {}, {}, true);
  }

  async createManualTransaction(payload: CreateManualTransactionPayload): Promise<ManualTransactionCreated> {
    return await this.httpClient.post<ManualTransactionCreated>('/wallets/manual-transactions', payload, true);
  }
}
